/**
 * Чистая логика вкладки «Видео» (спека §9.2 «Видео», §8; v2 §1.1–1.2, §1.6): набор запуска по моделям,
 * размер кадра модели одной строкой, чип политики лиц, запасные модели под родителем, маршрут кадров с
 * лицом, итоги оценки по моделям (с маршрутизированными и переиспользованными клипами), блокеры → кадр,
 * кнопка запуска, миниатюры чек-листа, буквы моделей хронологии, клипы для монтажа.
 *
 * Без импортов из `@/…`: модуль проверяется `node --test` (tests/videoRun.test.ts) как есть.
 * Правила — копии серверных: `videogen.frame_size`/`_res_px`, `videoclip._route` (кадр с лицом у модели
 * `rejects` → `FACE_FALLBACK`), `videoclip._shot_problem` (текст «кадр N: …»), `videocut._clip_model`/
 * `_best_clip` (чей готовый клип берёт монтаж модели).
 *
 * Среза «ниже подбородка» в панели нет (v2 §1.2): тело запуска не несёт `crops` — у сервера срез 0, и
 * кадры с лицом модели `rejects` уходят на запасную модель.
 */

/** Кадры чек-листа приёмки (§8): 0 с — постер, его в ряду не показываем. */
export const CHECK_FRAMES_MS = [1000, 2500, 5000] as const;

/** Чек-лист приёмки под рядом миниатюр — дословно §8. */
export const ACCEPTANCE_CHECKLIST =
  "платье то же (кружево, рукава, корсет, длина) · лицо одно · нет лишних людей · нет текста · камера одна · ткань движется, человек нет";

/** Колонок сравнения в ряд (§8), дальше — перенос. */
export const COMPARE_PER_ROW = 4;

type Faces = "unknown" | "accepts" | "rejects";

/** Модель справочника — ровно то, что нужно логике запуска. */
export type RunModel = {
  code: string;
  send: "size" | "resolution";
  default_size: string | null;
  sizes_9_16: string[];
  resolutions: string[];
  faces: Faces;
  discovered: boolean;
  price_error: string | null;
  /** Галочка в диалоге по умолчанию (v2 §1.1: только Kling 3.0 Pro). */
  default_on: boolean;
};

/** Строка модели в диалоге: выбрана, размер (или разрешение). */
export type RunPick = { on: boolean; size: string };

/** Набор запуска — форма `RunParams` из `lib/video.ts`; `crops` не шлём — срез 0, маршрутизация лиц. */
export type RunBody = {
  models: string[];
  sizes: Record<string, string>;
  seamless: boolean;
  with_refs: boolean;
};

/** Что предлагать в выборе размера: размеры 9:16 или разрешения — по `send`. */
export function sizeChoices(m: RunModel): string[] {
  return m.send === "size" ? m.sizes_9_16 : m.resolutions;
}

/** Модель нельзя выбрать: нет discovery или нет прайса — причина текстом; можно — `null`. */
export function modelBlock(m: RunModel): string | null {
  if (!m.discovered) return "нет discovery";
  if (m.price_error) return m.price_error;
  if (sizeChoices(m).length === 0) return "нет размеров 9:16";
  return null;
}

/** Начальная строка модели: галочка — `default_on`, размер — умолчание allowlist (если есть в каталоге). */
export function initialPick(m: RunModel): RunPick {
  const choices = sizeChoices(m);
  const size = m.default_size && choices.includes(m.default_size) ? m.default_size : (choices[0] ?? "");
  return { on: m.default_on, size };
}

/** Тело оценки/запуска: модели в порядке справочника, размер — явно у каждой выбранной. Ни одной — `null`. */
export function runBody(
  models: readonly RunModel[],
  picks: Readonly<Record<string, RunPick>>,
  seamless: boolean,
  withRefs: boolean,
): RunBody | null {
  const chosen = models.filter((m) => picks[m.code]?.on && modelBlock(m) === null);
  if (chosen.length === 0) return null;
  const sizes: Record<string, string> = {};
  for (const m of chosen) {
    const p = picks[m.code]!;
    if (p.size) sizes[m.code] = p.size;
  }
  return { models: chosen.map((m) => m.code), sizes, seamless, with_refs: withRefs };
}

/** `768p` → 768, `4K` → 2160; не понято — `null` (`videogen._res_px`). */
export function resPx(resolution: string): number | null {
  const v = resolution.trim().toLowerCase();
  if (v === "4k") return 2160;
  const n = Number(v.replace(/p$/, ""));
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Размер кадра под модель (`videogen.frame_size`): `WxH` как есть, у разрешения — (res, res × 16/9 до чётного). */
export function frameSize(send: "size" | "resolution", value: string): [number, number] | null {
  if (send === "size") {
    const m = /^(\d+)x(\d+)$/i.exec(value.trim());
    return m ? [Number(m[1]), Number(m[2])] : null;
  }
  const res = resPx(value);
  return res === null ? null : [res, Math.round((res * 16) / 9 / 2) * 2];
}

/** Размер одной строкой: «720p · 720×1280» (короткая сторона · кадр); не понят — как есть. */
export function sizeLabel(send: "size" | "resolution", value: string): string {
  const wh = frameSize(send, value);
  if (wh === null) return value;
  return `${Math.min(wh[0], wh[1])}p · ${wh[0]}×${wh[1]}`;
}

/** Чип политики лиц: «да / нет / ?» и подсказка; общая сноска под таблицей — `FACES_NOTE`. */
export const FACES_CHIP: Record<Faces, { text: string; title: string }> = {
  accepts: { text: "да", title: "принимает фото с лицом" },
  rejects: { text: "нет", title: "отклоняет фото с лицом — кадры с лицом уходят на запасную модель" },
  unknown: { text: "?", title: "неизвестно — первый клип будет пробой (отказ провайдера бесплатен)" },
};

export const FACES_NOTE =
  "Лица: «да» — модель принимает стилл с лицом; «нет» — отклоняет, кадры с лицом снимает запасная модель " +
  "(кадры без лица — она сама); «?» — ещё не знаем, первый клип — проба, отказ бесплатен.";

/** Куда уходят кадры с лицом у модели `rejects` (`videoclip._route`); у остальных — `null`. */
export function faceRoute(
  m: { faces: Faces; face_fallback: string | null },
  labelOf: (code: string) => string,
): string | null {
  if (m.faces !== "rejects") return null;
  return m.face_fallback ? `кадры с лицом → ${labelOf(m.face_fallback)}` : "кадры с лицом не примет — только кадры без лица";
}

/**
 * Строки таблицы моделей: запасная модель (`fallback_for`) — вложенной строкой сразу под родителем;
 * родителя в справочнике нет — на своём месте, как обычная.
 */
export function modelRows<M extends { code: string; fallback_for: string | null }>(
  models: readonly M[],
): { model: M; nested: boolean }[] {
  const codes = new Set(models.map((m) => m.code));
  const isChild = (m: M) => m.fallback_for !== null && m.fallback_for !== m.code && codes.has(m.fallback_for);
  const out: { model: M; nested: boolean }[] = [];
  for (const m of models) {
    if (isChild(m)) continue;
    out.push({ model: m, nested: false });
    for (const c of models) if (isChild(c) && c.fallback_for === m.code) out.push({ model: c, nested: true });
  }
  return out;
}

/** Пункт оценки/запуска: фактическая модель, исходная при маршрутизации, «клип уже есть». */
export type RouteItem = {
  shot_id: number;
  model: string;
  cost: number;
  routed_from?: string | null | undefined;
  reused?: boolean | undefined;
};

/**
 * Итог по строке таблицы моделей: ключ — модель, которую ВЫБРАЛИ (`routed_from` у маршрутизированного
 * пункта, иначе фактическая). `routed` — сколько кадров ушло на запасную, `reused` — сколько клипов
 * уже есть (без новой оплаты).
 */
export function modelTotals(
  items: readonly RouteItem[],
): Map<string, { count: number; cost: number; routed: number; reused: number }> {
  const out = new Map<string, { count: number; cost: number; routed: number; reused: number }>();
  for (const i of items) {
    const key = i.routed_from || i.model;
    const row = out.get(key) ?? { count: 0, cost: 0, routed: 0, reused: 0 };
    row.count += 1;
    row.cost += i.cost;
    if (i.routed_from) row.routed += 1;
    if (i.reused) row.reused += 1;
    out.set(key, row);
  }
  return out;
}

/**
 * Строки разбора для маршрутизированных и переиспользованных пунктов: «кадр 1: Seedance 2.0 → Kling 3.0
 * Pro (лицо в кадре) · ≈ $0.56» / «… · клип уже есть — без оплаты». Обычные пункты не перечисляются.
 */
export function routeLines(
  items: readonly RouteItem[],
  labelOf: (code: string) => string,
  orderOf: (shotId: number) => number | null,
): string[] {
  const out: string[] = [];
  for (const i of items) {
    if (!i.routed_from && !i.reused) continue;
    const n = orderOf(i.shot_id);
    const head = n === null ? `кадр #${i.shot_id}` : `кадр ${n}`;
    const what = i.routed_from
      ? `${labelOf(i.routed_from)} → ${labelOf(i.model)} (лицо в кадре)`
      : labelOf(i.model);
    const price = i.reused ? "клип уже есть — без оплаты" : `≈ $${i.cost.toFixed(2)}`;
    out.push(`${head}: ${what} · ${price}`);
  }
  return out;
}

/** Предупреждения сервера о маршруте лиц и повторе клипа — их показывает `routeLines`. */
const ROUTE_WARNING = /^(кадр с лицом: |тот же клип уже в этом запуске)/;

/** Предупреждения оценки без повторов (одно и то же у каждого кадра — одной строкой), кроме маршрутных. */
export function uniqueWarnings(items: readonly { warnings: readonly string[] }[]): string[] {
  return [...new Set(items.flatMap((i) => i.warnings))].filter((w) => !ROUTE_WARNING.test(w));
}

/** Блокер «кадр N: …» (`_shot_problem`) → id кадра по порядку; не про кадр — `null`. */
export function blockerShot(text: string, shots: readonly { shot_id: number; order: number }[]): number | null {
  const m = /^кадр (\d+):/.exec(text);
  if (!m) return null;
  return shots.find((s) => s.order === Number(m[1]))?.shot_id ?? null;
}

/**
 * Кнопка «Запустить» доступна, только когда оценка относится к ТЕКУЩЕМУ набору (`fresh` и ответ
 * получен без ошибки), блокеров нет, клипы есть и платный запрос не в полёте.
 */
export function canRun(s: {
  fresh: boolean;
  fetching: boolean;
  failed: boolean;
  blockers: number;
  clips: number;
  busy: boolean;
}): boolean {
  return s.fresh && !s.fetching && !s.failed && s.blockers === 0 && s.clips > 0 && !s.busy;
}

/** Миниатюры чек-листа клипа: кадры 1 / 2,5 / 5 с, что есть (ключи JSON — строки мс). */
export function checkFrames(frames: Readonly<Record<string, string>>): { ms: number; path: string }[] {
  return CHECK_FRAMES_MS.filter((ms) => frames[String(ms)]).map((ms) => ({ ms, path: frames[String(ms)]! }));
}

type ClipLike = { model: string; status: string; variant_id: number | null };
type ShotLike = {
  shot_id: number;
  order: number;
  duration_ms: number | null;
  /** `hidden` — лица в кадре нет; иначе (и у v1) — есть. */
  face?: string | undefined;
  clips: readonly ClipLike[];
};

/** Модели, у которых на хронологии есть хоть один клип, — в порядке справочника (колонки таблицы клипов). */
export function clipModels(timeline: readonly ShotLike[], order: readonly string[]): string[] {
  const present = new Set(timeline.flatMap((s) => s.clips.map((c) => c.model)));
  const known = order.filter((code) => present.has(code));
  return [...known, ...[...present].filter((code) => !order.includes(code)).sort()];
}

/** Готовые клипы кадра (вариант есть) — выбор для ручного монтажа. */
export function doneClips<C extends ClipLike>(shot: { clips: readonly C[] }): (C & { variant_id: number })[] {
  return shot.clips.filter((c): c is C & { variant_id: number } => c.status === "done" && c.variant_id !== null);
}

/**
 * Модели, монтаж которых собирается целиком: готовый клип нужной модели у КАЖДОГО кадра с длительностью.
 * Нужная — своя; у модели `rejects` для кадра с лицом — запасная (`faceFallback(code)`, `videocut._clip_model`).
 */
export function cuttableModels(
  timeline: readonly ShotLike[],
  order: readonly string[],
  faceFallback: (code: string) => string | null = () => null,
): string[] {
  const shots = timeline.filter((s) => s.duration_ms);
  if (shots.length === 0) return [];
  return clipModels(timeline, order).filter((code) =>
    shots.every((s) => {
      const fallback = s.face !== "hidden" ? faceFallback(code) : null;
      return doneClips(s).some((c) => c.model === (fallback ?? code));
    }),
  );
}

/**
 * Буквы моделей для чипов хронологии: первая буква подписи («Kling 3.0 Pro» → K); повтор буквы —
 * с номером по порядку справочника (Seedance 2.0 Mini → S, Seedance 2.0 → S2).
 */
export function modelLetters(codes: readonly string[], labelOf: (code: string) => string): Map<string, string> {
  const seen = new Map<string, number>();
  const out = new Map<string, string>();
  for (const code of codes) {
    if (out.has(code)) continue;
    const ch = (/[\p{L}\p{N}]/u.exec(labelOf(code))?.[0] ?? "?").toUpperCase();
    const n = (seen.get(ch) ?? 0) + 1;
    seen.set(ch, n);
    out.set(code, n === 1 ? ch : `${ch}${n}`);
  }
  return out;
}

/** Позиция общего ползунка, поставленная плееру: не дальше его длительности (NaN до метаданных — 0). */
export function seekTo(t: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return Math.min(Math.max(0, t), duration);
}

/** «1 клип / 2 клипа / 5 клипов»: форма по последним цифрам. */
export function plural(n: number, one: string, few: string, many: string): string {
  const d10 = n % 10;
  const d100 = n % 100;
  if (d10 === 1 && d100 !== 11) return one;
  if (d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)) return few;
  return many;
}

/**
 * Поле «потолок проекта, $» → значение для `video/settings`: пусто — `null` (без потолка), число ≥ 0
 * (запятая как точка) — округлённое до центов, иначе `undefined` — сохранять нечего (как 400 BAD_VALUE).
 */
export function parseCap(text: string): number | null | undefined {
  const t = text.trim().replace(",", ".");
  if (t === "") return null;
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(t)) return undefined;
  const value = Number(t);
  if (!Number.isFinite(value) || value < 0 || value >= 1e8) return undefined;
  return Math.round(value * 100) / 100;
}

/**
 * Строка «что выбрано» над кнопкой выбора моделей (задача Оли №335: экран начинался с таблицы на
 * семь строк). Модель и её размер — в порядке справочника; ничего не выбрано — пусто, и панель
 * скажет об этом сама.
 */
export function pickedSummary(
  models: readonly RunModel[],
  picks: Readonly<Record<string, RunPick>>,
  label: (code: string) => string,
): string[] {
  return models
    // Та же проверка, что в `runBody`: модель без discovery в запуск не пойдёт, и обещать её
    // строкой «модели: …» нельзя — рядом сразу «Выберите модели» (найдено ревью 16.10).
    .filter((m) => picks[m.code]?.on && modelBlock(m) === null)
    .map((m) => {
      const size = picks[m.code]?.size ?? "";
      return size ? `${label(m.code)} · ${sizeLabel(m.send, size)}` : label(m.code);
    });
}
